import { TFunction } from "i18next";
import { toast } from "react-toastify";

type ErrorResponse = {
  status?: number;
  data?: unknown;
};

type ErrorWithResponse = {
  response?: ErrorResponse;
};

type AllocatedItem = {
  id?: number;
  name: string;
};

type AllocatedForumGroup = {
  id?: number;
  name: string;
  questionnaires: AllocatedItem[];
  selectionSites: AllocatedItem[];
};

const getProperty = (value: unknown, camelCaseKey: string, pascalCaseKey: string): unknown => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const objectValue = value as Record<string, unknown>;
  return objectValue[camelCaseKey] ?? objectValue[pascalCaseKey];
};

const getString = (value: unknown, camelCaseKey: string, pascalCaseKey: string): string | undefined => {
  const property = getProperty(value, camelCaseKey, pascalCaseKey);
  return typeof property === "string" && property.trim() ? property : undefined;
};

const getNumber = (value: unknown, camelCaseKey: string, pascalCaseKey: string): number | undefined => {
  const property = getProperty(value, camelCaseKey, pascalCaseKey);
  return typeof property === "number" ? property : undefined;
};

const getAllocatedItems = (value: unknown): AllocatedItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const name = getString(item, "name", "Name");
    if (!name) {
      return [];
    }

    return [{ id: getNumber(item, "id", "Id"), name }];
  });
};

export const getForumGroupAllocationConflict = (error: unknown): AllocatedForumGroup[] | undefined => {
  const response = (error as ErrorWithResponse | undefined)?.response;
  if (response?.status !== 409) {
    return undefined;
  }

  const meta = getProperty(response.data, "meta", "Meta");
  const groups = getProperty(meta, "groups", "Groups");
  if (!Array.isArray(groups) || groups.length === 0) {
    return undefined;
  }

  const allocatedGroups = groups.flatMap((group) => {
    const name = getString(group, "name", "Name");
    if (!name) {
      return [];
    }

    const questionnaires = getAllocatedItems(getProperty(group, "questionnaires", "Questionnaires"));
    const selectionSites = getAllocatedItems(getProperty(group, "selectionSites", "SelectionSites"));
    if (questionnaires.length === 0 && selectionSites.length === 0) {
      return [];
    }

    return [
      {
        id: getNumber(group, "id", "Id"),
        name,
        questionnaires,
        selectionSites,
      },
    ];
  });

  return allocatedGroups.length > 0 ? allocatedGroups : undefined;
};

export const showForumGroupAllocationConflict = (error: unknown, t: TFunction): boolean => {
  const groups = getForumGroupAllocationConflict(error);
  if (!groups) {
    return false;
  }

  const reason = String(
    t("This change cannot be completed. Unallocate the following items before trying again."),
  );
  const questionnairesLabel = String(t("Questionnaires"));
  const selectionSitesLabel = String(t("Selection Sites"));

  toast.error(
    <div>
      <div>{reason}</div>
      <ul>
        {groups.map((group) => (
          <li key={group.id ?? group.name}>
            <strong>{group.name}</strong>
            {group.questionnaires.length > 0 && (
              <div>
                {`${questionnairesLabel}: ${group.questionnaires.map((item) => item.name).join(", ")}`}
              </div>
            )}
            {group.selectionSites.length > 0 && (
              <div>
                {`${selectionSitesLabel}: ${group.selectionSites.map((item) => item.name).join(", ")}`}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>,
    { autoClose: 30000 },
  );

  return true;
};
